import { useState, useEffect } from 'react'
import { LeadRule, LeadRuleCreate } from '../../services/leadRules.service'

interface RuleModalProps {
  rule?: LeadRule | null
  onSave: (data: LeadRuleCreate) => void
  onClose: () => void
  saving?: boolean
}

export default function RuleModal({ rule, onSave, onClose, saving }: RuleModalProps) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [criteria, setCriteria] = useState('')
  const [minConfidence, setMinConfidence] = useState(60)

  useEffect(() => {
    if (rule) {
      setName(rule.name)
      setDescription(rule.description || '')
      setCriteria(rule.criteria)
      setMinConfidence(rule.min_confidence)
    }
  }, [rule])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSave({
      name: name.trim(),
      description: description.trim() || undefined,
      criteria: criteria.trim(),
      min_confidence: minConfidence,
    })
  }

  const isValid = name.trim().length > 0 && criteria.trim().length >= 5

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <div className="p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">
              {rule ? 'Редактировать правило' : 'Новое правило'}
            </h2>

            {/* Name */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Название
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Например: Ищут разработчика"
                maxLength={200}
                className="input w-full"
                autoFocus
              />
            </div>

            {/* Description */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Описание <span className="text-gray-400">(необязательно)</span>
              </label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Краткое описание правила"
                className="input w-full"
              />
            </div>

            {/* Criteria */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Критерии поиска
              </label>
              <textarea
                value={criteria}
                onChange={(e) => setCriteria(e.target.value)}
                placeholder="Опишите, какие сообщения считать лидами. Например: Человек ищет разработчика, фрилансера или подрядчика для создания сайта, приложения или бота"
                rows={4}
                maxLength={2000}
                className="input w-full resize-none"
              />
              <p className="text-xs text-gray-400 mt-1">
                {criteria.length}/2000 символов. Мин. 5 символов.
              </p>
            </div>

            {/* Min Confidence */}
            <div className="mb-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Минимальная уверенность: {minConfidence}%
              </label>
              <input
                type="range"
                min={10}
                max={95}
                step={5}
                value={minConfidence}
                onChange={(e) => setMinConfidence(parseInt(e.target.value))}
                className="w-full accent-blue-600"
              />
              <div className="flex justify-between text-xs text-gray-400">
                <span>10% — больше лидов, ниже точность</span>
                <span>95% — меньше лидов, выше точность</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={!isValid || saving}
              className="btn-primary disabled:opacity-50"
            >
              {saving ? 'Сохранение...' : rule ? 'Сохранить' : 'Создать'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
